import { z } from "zod";
import { DataRoutes } from "./types";

const kanbanRoutes: DataRoutes = {
  // Get all boards accessible to current user (filtered by schema access)
  'get /api/data/kanban': {
    query: `
      SELECT 
        kb.Id as id,
        kb.UserId as userId,
        kb.SchemaId as schemaId,
        kb.Name as name,
        kb.Config as config,
        kb.CreatedAt as createdAt,
        kb.CreatedBy as createdBy,
        kb.UpdatedAt as updatedAt,
        kb.UpdatedBy as updatedBy,
        ds.Name as schemaName,
        ds.EntityType as entityType,
        u.Name as createdByName
      FROM KanbanBoards kb
      INNER JOIN DataSchema ds ON kb.SchemaId = ds.Id
      LEFT JOIN Users u ON kb.CreatedBy = u.Id
      WHERE ds.TeamId IN (
        SELECT TeamId FROM UserTeams 
        WHERE UserId = dbo.fnGetContextUser()
      ) OR ds.rlp = 1
      ORDER BY kb.UpdatedAt DESC
    `
  },

  // Get single board by ID
  'get /api/data/kanban/:id': {
    schema: z.object({
      id: z.number().int()
    }),
    queryOne: `
      SELECT 
        kb.Id as id,
        kb.UserId as userId,
        kb.SchemaId as schemaId,
        kb.Name as name,
        kb.Config as config,
        kb.CreatedAt as createdAt,
        kb.CreatedBy as createdBy,
        kb.UpdatedAt as updatedAt,
        kb.UpdatedBy as updatedBy,
        ds.Name as schemaName,
        ds.EntityType as entityType,
        u.Name as createdByName
      FROM KanbanBoards kb
      INNER JOIN DataSchema ds ON kb.SchemaId = ds.Id
      LEFT JOIN Users u ON kb.CreatedBy = u.Id
      WHERE kb.Id = @id
        AND (ds.TeamId IN (
          SELECT TeamId FROM UserTeams 
          WHERE UserId = dbo.fnGetContextUser()
        ) OR ds.rlp = 1)
    `
  },

  // Get boards for specific schema
  'get /api/data/kanban/schema/:schemaId': {
    schema: z.object({
      schemaId: z.number().int()
    }),
    query: `
      SELECT 
        kb.Id as id,
        kb.UserId as userId,
        kb.SchemaId as schemaId,
        kb.Name as name,
        kb.Config as config,
        kb.CreatedAt as createdAt,
        kb.CreatedBy as createdBy,
        kb.UpdatedAt as updatedAt,
        kb.UpdatedBy as updatedBy,
        ds.Name as schemaName,
        ds.EntityType as entityType,
        u.Name as createdByName
      FROM KanbanBoards kb
      INNER JOIN DataSchema ds ON kb.SchemaId = ds.Id
      LEFT JOIN Users u ON kb.CreatedBy = u.Id
      WHERE kb.SchemaId = @schemaId
        AND (ds.TeamId IN (
          SELECT TeamId FROM UserTeams 
          WHERE UserId = dbo.fnGetContextUser()
        ) OR ds.rlp = 1)
      ORDER BY kb.Name
    `
  },

  // Create new board
  'post /api/data/kanban': {
    schema: z.object({
      schemaId: z.number().int(),
      name: z.string().min(1).max(100),
      config: z.string() // JSON string
    }),
    queryOne: `
      -- Validate schema access
      IF NOT EXISTS (
        SELECT 1 FROM DataSchema 
        WHERE Id = @schemaId 
          AND (TeamId IN (
            SELECT TeamId FROM UserTeams 
            WHERE UserId = dbo.fnGetContextUser()
          ) OR rlp = 1)
      )
      BEGIN
        RAISERROR('Access denied to schema', 16, 1);
        RETURN;
      END

      -- Insert new board
      DECLARE @newId INT;
      INSERT INTO KanbanBoards (SchemaId, Name, Config, CreatedBy, UpdatedBy)
      VALUES (@schemaId, @name, @config, dbo.fnGetContextUser(), dbo.fnGetContextUser());
      SET @newId = SCOPE_IDENTITY();

      -- Return the created board with joined data
      SELECT 
        kb.Id as id,
        kb.UserId as userId,
        kb.SchemaId as schemaId,
        kb.Name as name,
        kb.Config as config,
        kb.CreatedAt as createdAt,
        kb.CreatedBy as createdBy,
        kb.UpdatedAt as updatedAt,
        kb.UpdatedBy as updatedBy,
        ds.Name as schemaName,
        ds.EntityType as entityType,
        u.Name as createdByName
      FROM KanbanBoards kb
      INNER JOIN DataSchema ds ON kb.SchemaId = ds.Id
      LEFT JOIN Users u ON kb.CreatedBy = u.Id
      WHERE kb.Id = @newId
    `
  },

  // Update board
  'patch /api/data/kanban/:id': {
    schema: z.object({
      id: z.number().int(),
      name: z.string().min(1).max(100).optional(),
      config: z.string().optional() // JSON string
    }),
    queryOne: `
      -- Validate schema access
      IF NOT EXISTS (
        SELECT 1 FROM KanbanBoards kb
        INNER JOIN DataSchema ds ON kb.SchemaId = ds.Id
        WHERE kb.Id = @id
          AND (ds.TeamId IN (
            SELECT TeamId FROM UserTeams 
            WHERE UserId = dbo.fnGetContextUser()
          ) OR ds.rlp = 1)
      )
      BEGIN
        RAISERROR('Access denied to board', 16, 1);
        RETURN;
      END

      -- Update board
      UPDATE KanbanBoards
      SET 
        Name = COALESCE(@name, Name),
        Config = COALESCE(@config, Config),
        UpdatedBy = dbo.fnGetContextUser(),
        UpdatedAt = GETDATE()
      WHERE Id = @id;

      -- Return updated board with joined data
      SELECT 
        kb.Id as id,
        kb.UserId as userId,
        kb.SchemaId as schemaId,
        kb.Name as name,
        kb.Config as config,
        kb.CreatedAt as createdAt,
        kb.CreatedBy as createdBy,
        kb.UpdatedAt as updatedAt,
        kb.UpdatedBy as updatedBy,
        ds.Name as schemaName,
        ds.EntityType as entityType,
        u.Name as createdByName
      FROM KanbanBoards kb
      INNER JOIN DataSchema ds ON kb.SchemaId = ds.Id
      LEFT JOIN Users u ON kb.CreatedBy = u.Id
      WHERE kb.Id = @id
    `
  },

  // Delete board
  'delete /api/data/kanban/:id': {
    schema: z.object({
      id: z.number().int()
    }),
    query: `
      -- Validate schema access
      IF NOT EXISTS (
        SELECT 1 FROM KanbanBoards kb
        INNER JOIN DataSchema ds ON kb.SchemaId = ds.Id
        WHERE kb.Id = @id
          AND (ds.TeamId IN (
            SELECT TeamId FROM UserTeams 
            WHERE UserId = dbo.fnGetContextUser()
          ) OR ds.rlp = 1)
      )
      BEGIN
        RAISERROR('Access denied to board', 16, 1);
        RETURN;
      END

      DELETE FROM KanbanBoards WHERE Id = @id
    `
  }
};

export default kanbanRoutes;
